import { Typography, Tooltip } from "@altimateai/lego";
import { cn } from "@/lib/utils";
import {
  FilterType,
  createAPIDropdownFilter,
  type APIFilterParams,
  type SortDirection,
  type TableHeaderType,
} from "@/components/tables/types";
import { formatBytes, formatCost } from "@/helpers/utils";
import type { UnifiedQuery, QueryStatementIdsResponse } from "../types";
import QueryTextWithHover from "@/pages/query/QueryText";
import { formatDatabricksCompute, formatDuration } from "./utils";
import dayjs from "dayjs";

interface FilterOption {
  label: string;
  value: string;
}

/** Server-side search for the Query ID dropdown (text typed -> matching IDs). */
export type QueryIdSearchFn = (
  params: APIFilterParams
) => Promise<QueryStatementIdsResponse | undefined>;

/** Tooltip-wrapped cell that truncates to the COLUMN's width, not to
 *  a fixed pixel cap. Used for the Query ID and User columns so that
 *  widening the column header surfaces more of the value instead of
 *  staying clipped at an arbitrary 100-120 px ceiling. The full text
 *  is still reachable on hover. */
const flexTruncatedCell = (text: string, extraClass = "") => (
  <Tooltip title={text}>
    <Typography size="sm" className={cn("al-truncate al-block al-w-full", extraClass)}>
      {text || "-"}
    </Typography>
  </Tooltip>
);

/** Plain small-text cell — used by the formatted-value columns. */
const textCell = (value: string) => <Typography size="sm">{value}</Typography>;

interface ColumnConfig {
  sortAttr: string;
  sortOrder: SortDirection;
  statementTypeFilter: string;
  statementIdFilter: string[];
  /** Unified Compute column filter — selected ``{kind}:{id}`` values. */
  computeFilter: string[];
  statementTypeOptions: FilterOption[];
  /** Unified Compute dropdown options (warehouse + cluster + pipeline). */
  computeOptions: FilterOption[];
  currency: string;
  /** Server-side search backing the Query ID dropdown. */
  queryIdSearchFn: QueryIdSearchFn;
  onSortChange: (attribute: string, direction: SortDirection) => void;
  onMultiFilterChange: (key: string, values: string[]) => void;
}

const buildSortConfig = (
  attribute: string,
  sortAttr: string,
  sortOrder: SortDirection,
  onSortChange: (attribute: string, direction: SortDirection) => void
) => ({
  onChange: (direction: SortDirection) => onSortChange(attribute, direction),
  value: sortAttr === attribute ? sortOrder : "",
});

export const getQueryTableColumns = (config: ColumnConfig): TableHeaderType<UnifiedQuery>[] => {
  const {
    sortAttr,
    sortOrder,
    statementTypeFilter,
    statementIdFilter,
    computeFilter,
    statementTypeOptions,
    computeOptions,
    currency,
    queryIdSearchFn,
    onSortChange,
    onMultiFilterChange,
  } = config;

  return [
    {
      id: 1,
      uid: "statement_text",
      label: "Query",
      width: "22%",
      render: (item: UnifiedQuery) => {
        const text = item.statement_text || "";
        const truncated = text.replace(/\s+/g, " ").trim();
        const display = truncated.length > 45 ? `${truncated.slice(0, 45)}...` : truncated || "-";
        return (
          <div className="al-overflow-hidden al-w-full">
            <QueryTextWithHover queryText={display} fullText={text} />
          </div>
        );
      },
    },
    {
      id: 2,
      uid: "statement_id",
      label: "Query ID",
      // Wider than the other id columns because statement_ids are UUIDs
      // (~36 chars); 8% truncated to ~12 chars at the default minWidth,
      // which made the cell mostly ``…``. 18% surfaces ~28 chars at
      // rest, which is enough to recognise + copy the prefix.
      width: "18%",
      // Searchable dropdown — statement IDs are high-cardinality, so the
      // options are fetched from the server as the user types rather than
      // loaded all at once.
      filter: createAPIDropdownFilter({
        filterType: FilterType.API_DROPDOWN,
        searchKey: "queryId",
        label: "Query ID",
        value: statementIdFilter,
        seeMore: true,
        apiFunction: queryIdSearchFn,
        extractorFunction: data =>
          (data?.statement_ids ?? []).map(id => ({ label: id, value: id })),
        onChange: (values: string[]) => onMultiFilterChange("queryId", values),
      }),
      // Truncates to the COLUMN width (not a hard 120 px cap) so
      // widening the column surfaces more of the id. Full id stays
      // available on hover via the tooltip.
      render: (item: UnifiedQuery) => flexTruncatedCell(item.statement_id || "", "al-font-mono"),
    },
    {
      id: 3,
      uid: "total_duration_ms",
      label: "Exec Time",
      width: "6%",
      sort: buildSortConfig("total_duration_ms", sortAttr, sortOrder, onSortChange),
      render: (item: UnifiedQuery) => textCell(formatDuration(item.total_duration_ms || 0)),
    },
    {
      id: 4,
      uid: "estimated_cost_usd",
      label: "Est. Cost",
      width: "6%",
      sort: buildSortConfig("estimated_cost_usd", sortAttr, sortOrder, onSortChange),
      // ``null`` is unattributed (mart's ``no_billing_window`` rows);
      // render ``"-"`` so the UI distinguishes that from a genuine
      // ``$0``. Coalescing to 0 would flatten the two.
      render: (item: UnifiedQuery) =>
        textCell(
          item.estimated_cost_usd == null ? "-" : formatCost(item.estimated_cost_usd, currency)
        ),
    },
    {
      // Databricks queries run on a SQL warehouse, an all-purpose / job
      // cluster, or serverless DLT / job compute. The "Compute" column
      // renders the resolved compute label the backend composes in
      // ``compute_display`` (priority: warehouse name → warehouse id →
      // cluster name → cluster id → ``DLT Pipeline: <name|id>`` →
      // ``Job: <name|id>`` → ``-``). The column filter dropdown lists
      // warehouse + cluster + pipeline entries (job is intentionally
      // excluded BE-side as high-cardinality + repetitive); selections
      // are sent as prefixed ``{kind}:{id}`` values under the
      // ``compute`` URL param and ORed by the BE.
      id: 5,
      uid: "warehouse_name",
      label: "Compute",
      // Wider than 8% so the ``DLT Pipeline: <name|id>`` / ``Job:
      // <name|id>`` fallback labels are readable at rest.
      width: "14%",
      // Compute is a composed display string (warehouse / cluster /
      // pipeline / job / source-of-origin) — no single backend column
      // matches it, so the column is not sortable. Showing a sort
      // toggle that the BE would silently re-map is worse than no
      // toggle at all.
      filter:
        computeOptions.length > 0
          ? {
              filterType: FilterType.DROPDOWN,
              searchKey: "compute",
              // Filter label is just the metric name per the
              // altimate-frontend review rules (§3).
              label: "Compute",
              options: computeOptions,
              value: computeFilter,
              onChange: (values: string[]) => onMultiFilterChange("compute", values),
            }
          : undefined,
      render: (item: UnifiedQuery) => (
        // Render full text without truncation — long values (e.g. job
        // display names) wrap to multiple lines instead of being
        // clipped. The fallback chain lives in
        // ``formatDatabricksCompute`` so the list cell and the
        // detail-page Compute metric stay in lockstep.
        <Typography size="sm" className="al-break-words al-whitespace-normal">
          {formatDatabricksCompute(item)}
        </Typography>
      ),
    },
    {
      id: 7,
      uid: "statement_type",
      label: "Query Type",
      width: "7%",
      sort: buildSortConfig("statement_type", sortAttr, sortOrder, onSortChange),
      filter:
        statementTypeOptions.length > 0
          ? {
              filterType: FilterType.DROPDOWN_SINGLE,
              searchKey: "statementType",
              label: "Query Type",
              options: statementTypeOptions,
              value: statementTypeFilter,
            }
          : undefined,
      render: (item: UnifiedQuery) => textCell(item.statement_type || "-"),
    },
    {
      // ``Data Read``, ``User`` and ``Workspace`` used to live here but
      // were too noisy at row level on a high-density table; the values
      // moved to the per-query detail page metadata grid where there's
      // room to render them in full. ``User`` and ``Workspace`` are
      // still server-side filterable via the More Filters sheet —
      // removing the column header dropped the column's filter UI
      // only, not the underlying BE filter wiring.
      id: 8,
      uid: "spilled_local_bytes",
      label: "Data Spilled",
      width: "6%",
      sort: buildSortConfig("spilled_local_bytes", sortAttr, sortOrder, onSortChange),
      render: (item: UnifiedQuery) => textCell(formatBytes(item.spilled_local_bytes || 0)),
    },
    {
      id: 9,
      uid: "start_time",
      label: "Timestamp",
      width: "7%",
      sort: buildSortConfig("start_time", sortAttr, sortOrder, onSortChange),
      render: (item: UnifiedQuery) =>
        textCell(item.start_time ? dayjs(item.start_time).format("MMM D, YYYY HH:mm") : "-"),
    },
  ];
};
